import { useEffect, useState } from "react";
import { Download, FilePlus2 } from "lucide-react";
import { toast } from "sonner";
import { api, toastError } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { buildTenantSetupSummaryPdf, type SetupSummaryDocument } from "../../../lib/tenantSetupSummaryPdf";

export function SetupSummaryDocuments() {
  const [rows, setRows] = useState<SetupSummaryDocument[]>([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { setRows((await api.get("/onboarding/wizard/setup-summary-documents")).data); }
    catch (error) { toastError(error); }
  };
  useEffect(() => { void load(); }, []);

  const savePdf = (row: SetupSummaryDocument) => {
    const blob = buildTenantSetupSummaryPdf(row);
    const url = URL.createObjectURL(blob); const link = document.createElement("a");
    link.href = url;
    link.download = `tenant-setup-summary-${row.snapshot.tenant.slug}-v${row.version_number}.pdf`;
    link.click(); URL.revokeObjectURL(url);
  };
  const download = async (id: string) => {
    setBusy(true);
    try {
      const row = (await api.post(`/onboarding/wizard/setup-summary-documents/${id}/download`)).data as SetupSummaryDocument;
      savePdf(row); toast.success("Tenant Setup Summary PDF downloaded"); await load();
    } catch (error) { toastError(error); } finally { setBusy(false); }
  };
  const generate = async () => {
    setBusy(true);
    try {
      const row = (await api.post("/onboarding/wizard/setup-summary-documents")).data as SetupSummaryDocument;
      savePdf(row); toast.success(`Setup Summary v${row.version_number} generated`); await load();
    } catch (error) { toastError(error); } finally { setBusy(false); }
  };

  return <div className="mt-4 rounded-lg border border-slate-200 p-4" data-testid="setup-summary-documents">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div><h4 className="font-semibold">Tenant Setup Summary</h4><p className="text-xs text-slate-500">Password-free, versioned snapshots remain available for audit and re-download.</p></div>
      <Button onClick={() => void generate()} disabled={busy}><FilePlus2 className="mr-1 h-4 w-4" /> Generate new version</Button>
    </div>
    <div className="mt-3 space-y-2">
      {rows.length === 0 ? <p className="text-sm text-slate-500">No setup summary has been generated.</p> : rows.map((row) =>
        <div key={row.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-2 text-sm">
          <div><span className="font-medium">Version {row.version_number}</span><span className="ml-2 text-slate-500">{new Date(row.generated_at).toLocaleString()} · {row.generated_by_name || "Owner"} · {row.download_count} download(s)</span></div>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => void download(row.id)}><Download className="mr-1 h-3.5 w-3.5" /> Download again</Button>
        </div>)}
    </div>
  </div>;
}
